import React, { useState, useEffect } from 'react';
import { 
  Mic, Headphones, Video, Volume2, Play, ArrowRight, Check,
  Activity, Sparkles, Smile, RefreshCw, Layers
} from 'lucide-react';

interface HeroClinicalShowcaseProps {
  onSelectModule?: (moduleId: string) => void;
  onExploreDemo?: (tab: string) => void;
}

export const HeroClinicalShowcase: React.FC<HeroClinicalShowcaseProps> = ({
  onSelectModule,
  onExploreDemo
}) => {
  // Card 1: Interactive Wave & Balloon State
  const [balloonElevation, setBalloonElevation] = useState(45);
  const [balloonPitch, setBalloonPitch] = useState(120);
  const [balloonDb, setBalloonDb] = useState(65);
  const [wavePhase, setWavePhase] = useState(0);

  // Card 3: Articulation Mirror state
  const [mirrorPhoneme, setMirrorPhoneme] = useState<'/s/' | '/r/' | '/th/'>('/s/');
  const [isMirrorActive, setIsMirrorActive] = useState(false);

  // Card 4: Mini AAC Sentence Strip state
  const [sentence, setSentence] = useState<string[]>(['I', 'want', 'to', 'eat']);

  // Gentle wave animation tick
  useEffect(() => {
    const timer = setInterval(() => {
      setWavePhase(p => (p + 0.15) % (Math.PI * 2));
      // Gentle natural jitter
      setBalloonElevation(prev => Math.min(80, Math.max(20, prev + (Math.random() * 6 - 3))));
      setBalloonPitch(prev => Math.round(118 + Math.sin(Date.now() / 400) * 8));
      setBalloonDb(prev => Math.round(62 + Math.cos(Date.now() / 500) * 5));
    }, 100);
    return () => clearInterval(timer);
  }, []);

  const handleAddAacWord = (word: string) => {
    setSentence(prev => [...prev, word]);
    if ('speechSynthesis' in window) {
      const u = new SpeechSynthesisUtterance(word);
      window.speechSynthesis.speak(u);
    }
  };

  const handleSpeakSentence = () => {
    if ('speechSynthesis' in window && sentence.length > 0) {
      const u = new SpeechSynthesisUtterance(sentence.join(' '));
      window.speechSynthesis.speak(u);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      
      {/* 4-Card Grid: Responsive Desktop (4 col) / Tablet (2 col) / Mobile (1 col with scroll) */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        
        {/* ================================================================= */}
        {/* CARD 1: Dr. Speech Dual Waveform & Voice Balloon                  */}
        {/* ================================================================= */}
        <div 
          onClick={() => onSelectModule && onSelectModule('speech-therapy')}
          className="bg-[#fbf9f5] rounded-[32px] p-6 border border-black/10 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col justify-between cursor-pointer group hover:-translate-y-1 relative overflow-hidden"
        >
          <div>
            <h3 className="font-extrabold text-base text-[#1c1b1f] leading-snug tracking-tight mb-3">
              Dr. Speech Real-Time Dual Waveform & Voice Balloon
            </h3>

            {/* Inner Dual-Waveform Display Box */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-inner mb-4 relative">
              
              {/* Upper Patient Waveform */}
              <div className="mb-3">
                <div className="flex items-center justify-between text-[10px] font-mono font-bold text-slate-500 mb-1">
                  <span>Patient</span>
                  <span className="text-[#ffd64e] font-black">● Target</span>
                </div>
                
                {/* SVG Live Waveform */}
                <svg className="w-full h-10 overflow-visible" viewBox="0 0 200 40">
                  <path
                    d={`M 0 20 Q 25 ${20 + Math.sin(wavePhase) * 14}, 50 20 T 100 20 T 150 20 T 200 20`}
                    fill="none"
                    stroke="#1c1b1f"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d={`M 0 20 Q 25 ${20 + Math.cos(wavePhase * 1.2) * 8}, 50 20 T 100 20 T 150 20 T 200 20`}
                    fill="none"
                    stroke="#ffd64e"
                    strokeWidth="1.5"
                    strokeDasharray="3 3"
                  />
                </svg>
              </div>

              {/* Lower Reference Waveform */}
              <div className="pt-2 border-t border-slate-100">
                <svg className="w-full h-8 overflow-visible opacity-70" viewBox="0 0 200 30">
                  <path
                    d={`M 0 15 Q 20 ${15 + Math.sin(wavePhase * 0.8) * 10}, 40 15 T 80 15 T 120 15 T 160 15 T 200 15`}
                    fill="none"
                    stroke="#64748b"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>

              {/* Real-Time Floating Balloon */}
              <div 
                className="absolute right-6 transition-all duration-300 ease-out pointer-events-none"
                style={{ bottom: `${balloonElevation}%` }}
              >
                <div className="relative flex flex-col items-center">
                  <div className="w-10 h-12 bg-gradient-to-tr from-sky-500 to-indigo-400 rounded-[50%_50%_50%_50%/60%_60%_40%_40%] shadow-lg animate-pulse" />
                  <div className="w-2 h-1 bg-amber-800 -mt-0.5 rounded-sm" />
                  <div className="w-0.5 h-6 bg-slate-400/80" />
                </div>
              </div>

            </div>

            {/* Pitch & Intensity Telemetry Badges */}
            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="bg-white/80 p-2.5 rounded-xl border border-slate-200">
                <div className="text-[10px] uppercase font-bold text-slate-400">Pitch</div>
                <div className="font-extrabold text-sm text-[#1c1b1f] font-mono">{balloonPitch} Hz</div>
              </div>
              <div className="bg-white/80 p-2.5 rounded-xl border border-slate-200">
                <div className="text-[10px] uppercase font-bold text-slate-400">Intensity</div>
                <div className="font-extrabold text-sm text-[#1c1b1f] font-mono">{balloonDb} dB</div>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between text-xs font-bold text-[#1c1b1f]">
            <span>Dr. Speech Studio</span>
            <span className="text-[#1c1b1f] flex items-center gap-1 group-hover:translate-x-1 transition-transform">
              Launch Studio →
            </span>
          </div>
        </div>

        {/* ================================================================= */}
        {/* CARD 2: Pure-Tone Audiometer & Speech Banana                      */}
        {/* ================================================================= */}
        <div 
          onClick={() => onSelectModule && onSelectModule('audiometry-hearing')}
          className="bg-[#fbf9f5] rounded-[32px] p-6 border border-black/10 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col justify-between cursor-pointer group hover:-translate-y-1"
        >
          <div>
            <h3 className="font-extrabold text-base text-[#1c1b1f] leading-snug tracking-tight mb-3">
              Pure-Tone Audiometer with Interactive Speech Banana
            </h3>

            {/* Audiogram Canvas Container */}
            <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-inner mb-4">
              <div className="text-[10px] font-mono text-center font-bold text-slate-400 mb-1">
                Frequency (Hz) : 125 • 250 • 500 • 1k • 2k • 4k • 8k
              </div>

              {/* SVG Audiogram Grid + Speech Banana + Plotted Points */}
              <svg className="w-full h-36" viewBox="0 0 220 130">
                {/* Grid lines */}
                {[20, 45, 70, 95, 120].map((y, i) => (
                  <line key={i} x1="20" y1={y} x2="210" y2={y} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="2 2" />
                ))}
                {[45, 80, 115, 150, 185].map((x, i) => (
                  <line key={i} x1={x} y1="15" x2={x} y2="120" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="2 2" />
                ))}

                {/* Shaded Yellow "Speech Banana" Zone */}
                <path
                  d="M 45 35 Q 115 50 185 30 Q 190 70 150 78 Q 80 82 45 55 Z"
                  fill="#ffd64e"
                  fillOpacity="0.45"
                  stroke="#ffd64e"
                  strokeWidth="1.5"
                />

                {/* Speech Banana Phoneme Labels */}
                <text x="60" y="50" fontSize="8" fontWeight="bold" fill="#713f12">m, b, d</text>
                <text x="105" y="65" fontSize="8" fontWeight="bold" fill="#713f12">e, a, o</text>
                <text x="145" y="48" fontSize="8" fontWeight="bold" fill="#713f12">s, f, th</text>

                {/* Right Ear Thresholds (Red Circles 'O') */}
                <circle cx="50" cy="28" r="4" fill="none" stroke="#ef4444" strokeWidth="2" />
                <circle cx="95" cy="30" r="4" fill="none" stroke="#ef4444" strokeWidth="2" />
                <circle cx="135" cy="35" r="4" fill="none" stroke="#ef4444" strokeWidth="2" />

                {/* Left Ear Thresholds (Blue Crosses 'X') */}
                <g stroke="#3b82f6" strokeWidth="2">
                  <line x1="160" y1="28" x2="168" y2="36" />
                  <line x1="168" y1="28" x2="160" y2="36" />
                  <line x1="185" y1="32" x2="193" y2="40" />
                  <line x1="193" y1="32" x2="185" y2="40" />
                </g>

                {/* Axis Labels */}
                <text x="5" y="30" fontSize="8" fill="#94a3b8" fontFamily="monospace">10</text>
                <text x="5" y="70" fontSize="8" fill="#94a3b8" fontFamily="monospace">50</text>
                <text x="5" y="115" fontSize="8" fill="#94a3b8" fontFamily="monospace">90</text>
              </svg>

              <div className="flex items-center justify-between text-[10px] font-bold px-2 pt-1 border-t border-slate-100">
                <span className="text-rose-500 font-mono">🔴 Right Ear (O): 20dB</span>
                <span className="text-blue-500 font-mono">🔵 Left Ear (X): 25dB</span>
              </div>
            </div>

            <div className="px-3 py-1.5 bg-[#bfecc7]/30 border border-[#bfecc7] text-emerald-900 rounded-xl text-[11px] font-bold text-center">
              ✓ Normal Speech Range Confirmed
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between text-xs font-bold text-[#1c1b1f]">
            <span>Pure-Tone Screener</span>
            <span className="text-[#1c1b1f] flex items-center gap-1 group-hover:translate-x-1 transition-transform">
              Open Screener →
            </span>
          </div>
        </div>

        {/* ================================================================= */}
        {/* CARD 3: WebRTC Digital Articulation Mirror                        */}
        {/* ================================================================= */}
        <div 
          onClick={() => onSelectModule && onSelectModule('speech-therapy')}
          className="bg-[#fbf9f5] rounded-[32px] p-6 border border-black/10 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col justify-between cursor-pointer group hover:-translate-y-1"
        >
          <div>
            <h3 className="font-extrabold text-base text-[#1c1b1f] leading-snug tracking-tight mb-3">
              WebRTC Digital Articulation Mirror
            </h3>

            {/* Anatomical Camera / Facial Alignment Box */}
            <div className="bg-slate-900 rounded-2xl p-3 aspect-[4/3] flex flex-col items-center justify-center relative overflow-hidden mb-4 shadow-inner">
              
              {/* Face/Lip Tracking Wireframe Overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <svg className="w-36 h-28 opacity-60" viewBox="0 0 100 80">
                  {/* Facial cross-hairs */}
                  <line x1="50" y1="10" x2="50" y2="70" stroke="#ffd64e" strokeWidth="0.8" strokeDasharray="2 2" />
                  <line x1="15" y1="40" x2="85" y2="40" stroke="#ffd64e" strokeWidth="0.8" strokeDasharray="2 2" />
                  
                  {/* Lip contour wireframe */}
                  <ellipse cx="50" cy="40" rx="25" ry="14" fill="none" stroke="#38bdf8" strokeWidth="1.5" />
                  <ellipse cx="50" cy="40" rx="16" ry="6" fill="#38bdf8" fillOpacity="0.2" stroke="#38bdf8" strokeWidth="1" />
                  
                  {/* Acoustic tracking anchor dots */}
                  <circle cx="25" cy="40" r="2" fill="#ffd64e" />
                  <circle cx="75" cy="40" r="2" fill="#ffd64e" />
                  <circle cx="50" cy="26" r="2" fill="#ffd64e" />
                  <circle cx="50" cy="54" r="2" fill="#ffd64e" />
                </svg>
              </div>

              {/* Floating Phoneme Target Badges */}
              <div className="absolute top-3 left-3 text-[11px] font-mono font-black px-2 py-0.5 rounded-full bg-white/20 text-white backdrop-blur-sm">
                Initial <span className="text-[#ffd64e]">{mirrorPhoneme}</span>
              </div>
              <div className="absolute top-3 right-3 text-[11px] font-mono font-black px-2 py-0.5 rounded-full bg-white/20 text-white backdrop-blur-sm">
                Final <span className="text-sky-300">/t/</span>
              </div>

              {/* Start Mirror CTA inside Card */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMirrorActive(!isMirrorActive);
                }}
                className="relative z-10 px-5 py-2 rounded-full bg-white text-black font-extrabold text-xs shadow-lg hover:bg-[#ffd64e] transition-colors cursor-pointer"
              >
                {isMirrorActive ? 'CAMERA ACTIVE' : 'START MIRROR'}
              </button>

              <div className="absolute bottom-2 text-[9px] font-mono text-slate-400">
                Front-Camera Video Feedback
              </div>
            </div>

            {/* Quick Phoneme Selectors */}
            <div className="flex items-center justify-center gap-2">
              {(['/s/', '/r/', '/th/'] as const).map(p => (
                <button
                  key={p}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMirrorPhoneme(p);
                  }}
                  className={`px-3 py-1 rounded-full text-xs font-extrabold transition-all ${
                    mirrorPhoneme === p 
                      ? 'bg-[#1c1b1f] text-white shadow-sm' 
                      : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between text-xs font-bold text-[#1c1b1f]">
            <span>Digital Mirror</span>
            <span className="text-[#1c1b1f] flex items-center gap-1 group-hover:translate-x-1 transition-transform">
              Launch Mirror →
            </span>
          </div>
        </div>

        {/* ================================================================= */}
        {/* CARD 4: AAC Communication Board                                   */}
        {/* ================================================================= */}
        <div 
          onClick={() => onSelectModule && onSelectModule('aac')}
          className="bg-[#fbf9f5] rounded-[32px] p-6 border border-black/10 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col justify-between cursor-pointer group hover:-translate-y-1"
        >
          <div>
            <h3 className="font-extrabold text-base text-[#1c1b1f] leading-snug tracking-tight mb-3">
              AAC Communication Board
            </h3>

            {/* Upper Sentence Strip Accumulator */}
            <div 
              onClick={(e) => {
                e.stopPropagation();
                handleSpeakSentence();
              }}
              className="bg-white p-2.5 rounded-2xl border border-slate-200 shadow-inner mb-3 flex items-center justify-between gap-2 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-1.5 overflow-hidden">
                <span className="text-sm">😊</span>
                <span className="text-xs font-bold text-[#1c1b1f] truncate">
                  {sentence.join(' ') || 'Tap words below...'}
                </span>
              </div>
              <div className="w-6 h-6 rounded-full bg-[#1c1b1f] text-white flex items-center justify-center shrink-0">
                <Play className="w-3 h-3 fill-current ml-0.5" />
              </div>
            </div>

            {/* 3x3 Mini Fitzgerald Key Grid */}
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { label: 'Hello', cat: 'social', bg: 'bg-rose-100 border-rose-300 text-rose-900', icon: '👋' },
                { label: 'Yes', cat: 'social', bg: 'bg-emerald-100 border-emerald-300 text-emerald-900', icon: '✓' },
                { label: 'No', cat: 'social', bg: 'bg-rose-100 border-rose-300 text-rose-900', icon: '✕' },
                { label: 'Eat', cat: 'verb', bg: 'bg-emerald-50 border-emerald-200 text-emerald-900', icon: '🍽️' },
                { label: 'Drink', cat: 'verb', bg: 'bg-sky-100 border-sky-300 text-sky-900', icon: '🥤' },
                { label: 'Help', cat: 'social', bg: 'bg-amber-100 border-amber-300 text-amber-900', icon: '❓' },
                { label: 'Water', cat: 'noun', bg: 'bg-orange-100 border-orange-300 text-orange-900', icon: '💧' },
                { label: 'More', cat: 'action', bg: 'bg-yellow-100 border-yellow-300 text-yellow-900', icon: '➕' },
                { label: 'Rest', cat: 'state', bg: 'bg-indigo-100 border-indigo-300 text-indigo-900', icon: '🛌' }
              ].map((item, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddAacWord(item.label);
                  }}
                  className={`p-2 rounded-xl border text-center transition-transform active:scale-95 shadow-2xs hover:brightness-95 cursor-pointer ${item.bg}`}
                >
                  <div className="text-base leading-none mb-0.5">{item.icon}</div>
                  <div className="text-[10px] font-extrabold truncate">{item.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between text-xs font-bold text-[#1c1b1f]">
            <span>AAC Speech Board</span>
            <span className="text-[#1c1b1f] flex items-center gap-1 group-hover:translate-x-1 transition-transform">
              Open Board →
            </span>
          </div>
        </div>

      </div>

    </div>
  );
};
